/**
 * Shared at-a-glance lane summary helpers for the grouped-display collapsed row
 * (Task 5b, Quotes — Grouped Lane Display). Derives an all-in Total from the
 * Task-5a pricing fields on a `QuoteListRow`.
 *
 * IMPORTANT — this is an AT-A-GLANCE summary, not the authoritative invoice
 * math. It sums the dray-with-charges base + the FSC/fuel contribution it can
 * compute statically from the row. It deliberately does NOT include every
 * warehouse handling/storage charge, nor FSC types that require a tariff or
 * mileage lookup (CURRENT / CUSTOMER / DISCOUNTED / MILES) — those resolve at
 * invoice time. That approximation is acceptable for the collapsed lane row;
 * the expanded Direct detail + the PDF are the sources of truth.
 *
 * FSC token semantics (authoritative source: backend quote_pdf/render.py
 * `_fsc_suffix` + invoice_automation.pl). DIRECT quotes carry fscRate/fscType;
 * WAREHOUSE quotes carry a dollar fuelSurcharge.
 *   PCT%       → percent of dray:  FSC = drayWithCharges * fscRate/100
 *   FLAT       → flat dollars:     FSC = fscRate
 *   MILES      → $rate per mile (miles not on the row → no Total contribution)
 *   CURRENT / CUSTOMER / DISCOUNTED → tariff/discount lookup (no Total contribution)
 */
import type { QuoteListRow } from './fixtures';

/** FSC types whose dollar contribution is the rate read as a percent of dray. */
const PERCENT_FSC_TYPE = 'PCT%';
/** FSC types whose dollar contribution is the flat rate value itself. */
const FLAT_FSC_TYPE = 'FLAT';

/** Parse a decimal-string field safely. Returns null for ""/undefined/garbage. */
function parseDecimal(v: string | undefined | null): number | null {
  const s = (v ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * All-in Total for the collapsed lane row, or null when the dray base is
 * missing/non-numeric (so the row can render a placeholder instead of "$0").
 *   DIRECT:    drayWithCharges + FSC (PCT% → base*rate/100, FLAT → rate)
 *   WAREHOUSE: drayWithCharges + fuelSurcharge (dollars)
 * Non-computable DIRECT FSC types contribute 0 to the at-a-glance Total.
 */
export function laneAllInTotal(row: QuoteListRow): number | null {
  const base = parseDecimal(row.drayWithCharges);
  if (base == null) return null;

  if (row.quoteType === 'WAREHOUSE') {
    const fuel = parseDecimal(row.fuelSurcharge) ?? 0;
    return base + fuel;
  }

  // DIRECT (and any other type carrying fscRate/fscType).
  const rate = parseDecimal(row.fscRate);
  const type = (row.fscType ?? '').trim().toUpperCase();
  if (rate == null) return base;
  if (type === PERCENT_FSC_TYPE) return base + (base * rate) / 100;
  if (type === FLAT_FSC_TYPE) return base + rate;
  // CURRENT / CUSTOMER / DISCOUNTED / MILES → not statically computable here.
  return base;
}
